import type * as ents from '@attain-apps/entities'
import * as excel from '@attain-apps/excel'
import type * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as webExcel from '@attain-apps/fe/web/main/lib/excel'

import * as common from '../../common'
import type { Entity, TypeParams } from '../types'

const SHEET_NAME = 'Rules'

/**
 * Generates an Excel workbook for exported GL records.
 *
 * The workbook contains one sheet with rows expanded for mapping entries when a
 * record has mapping changes.
 */
export function getWorkbook(
  params: Readonly<webExcel.ec.models.Download.Producer.Params<TypeParams>>,
): webExcel.Workbook {
  return new webExcel.Workbook(getSheet(params))
}

/**
 * Creates the GL records export sheet.
 *
 * Rows keep a reference to the original transformed record so normal attributes
 * can use the same value extractors as the table, while mapping-specific
 * attributes can read from a flattened mapping entry.
 */
function getSheet({
  attributes,
  entities,
}: Readonly<
  webExcel.ec.models.Download.Producer.Params<TypeParams>
>): excel.Sheet {
  return new excel.Sheet(
    SHEET_NAME,
    new excel.Table<Row>(
      attributes.map(attribute => {
        const patch = MAPPING_ATTRIBUTE_PATCHES[attribute.id]
        return {
          ...attribute,
          getValueFrom:
            patch?.getValueFrom ??
            (({ record }) => attribute.getValueFrom(record)),
        }
      }),
      entities.flatMap<Row>(record => {
        if (record?.originalMapping?.entries.length) {
          // For records with mapping entries, we want to have one row for each
          // mapping entry
          return record.originalMapping.entries.map(entry => ({
            entry,
            record,
          }))
        }
        // For records without mapping entries, we just want one row for the
        // record
        return { record }
      }),
    ),
  )
}

interface Row {
  record: Entity
  entry?: ents.extra.rate.gl.Mapping.Entry
}

const MAPPING_ATTRIBUTE_PATCHES = {
  [common.attributes.mapping.Id.ExclusionLabel]: {
    getValueFrom: row => row.entry?.exclusionLabel,
  },
  [common.attributes.mapping.Id.IdcCategory]: {
    getValueFrom: row =>
      common.attributes.mapping.getIdcCategoryValue(
        row.entry?.faFunctionCategorization,
      ),
  },
  [common.attributes.mapping.Id.IdcSubcategory]: {
    getValueFrom: row =>
      common.attributes.mapping.getIdcSubcategoryValue(
        row.entry?.faFunctionCategorization,
      ),
  },
} satisfies Partial<
  Record<common.attributes.mapping.Id, MappingAttributePatch>
> as Generalize.Lookup<common.attributes.mapping.Id, MappingAttributePatch>

type MappingAttributePatch = Pick<
  ec.models.Attribute<Replace.Properties<TypeParams, 'entity', Row>>,
  'getValueFrom'
>
